import { DOCUMENT } from '@angular/common';
import { Injectable, NgZone, inject } from '@angular/core';
import { Router } from '@angular/router';
import { IStepOption, TourService } from 'ngx-ui-tour-core';
import { BehaviorSubject, Subject } from 'rxjs';

export type TutorialPlacement = 'top' | 'right' | 'bottom' | 'left';

export interface TutorialStep extends IStepOption {
  stepId: string;
  anchorId: string;
  eyebrow: string;
  title: string;
  body: string;
  placement: TutorialPlacement;
}

export type TutorialMode = 'automatic' | 'replay';
export type TutorialOutcome = 'completed' | 'skipped';
export interface TutorialEndEvent { mode: TutorialMode; outcome: TutorialOutcome; }

const steps: TutorialStep[] = [
  { stepId: 'find-vessel', anchorId: 'find-vessel', eyebrow: '01 / FIND A VESSEL', title: 'Find a vessel.', body: 'Enter a 7-digit IMO or a vessel name, then select RESOLVE.', placement: 'bottom' },
  { stepId: 'search-area', anchorId: 'search-area', eyebrow: '02 / OR SEARCH AN AREA', title: 'Or search an area.', body: 'Choose Select area, drag across the map, then search the selected area. Area searches are limited to 50 nautical miles.', placement: 'top' },
  { stepId: 'vessel-on-map', anchorId: 'vessel-on-map', eyebrow: '03 / SEE THE VESSELS ON THE MAP', title: 'See the vessels on the map.', body: 'Area-search results appear as markers. Select a vessel to open its available details.', placement: 'left' },
  { stepId: 'vessel-summary', anchorId: 'vessel-summary', eyebrow: '04 / YOUR VESSEL, IN ONE PANEL', title: 'Your vessel, in one panel.', body: 'Review the available identity, type, movement and latest AIS information. On a phone, this panel appears below the map.', placement: 'left' },
  { stepId: 'vessel-records', anchorId: 'vessel-records', eyebrow: '05 / EXPLORE VESSEL RECORDS', title: 'Explore vessel records.', body: 'Open the available voyages, inspections, casualties, dry dock, certificates, specifications and sales records.', placement: 'left', isAsync: true },
  { stepId: 'report-products', anchorId: 'report-products', eyebrow: '06 / REQUEST A VESSEL UCB', title: 'Request a Vessel UCB.', body: 'Select OPEN to continue to the real Vessel UCB request flow for the selected vessel.', placement: 'left', isAsync: true },
  { stepId: 'track-vessel', anchorId: 'track-vessel', eyebrow: '07 / TRACK A VESSEL', title: 'Track a vessel.', body: 'Use this control to save the selected vessel without purchasing a report. It will appear in Tracked vessels.', placement: 'left', isAsync: true },
  { stepId: 'tracked-vessels', anchorId: 'tracked-vessels', eyebrow: '08 / RETURN TO SAVED VESSELS', title: 'Return to saved vessels.', body: 'Tracked vessels and vessels linked to your reports are available here for quick access.', placement: 'right' },
  { stepId: 'case-workspace', anchorId: 'case-workspace', eyebrow: '09 / INTELLIGENCE CASES', title: 'Find your reports.', body: 'Open Intelligence cases to review report requests and open reports when they are ready.', placement: 'right' }
];

@Injectable({ providedIn: 'root' })
export class TutorialService {
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  private readonly zone = inject(NgZone);
  private readonly tour = inject(TourService);
  private readonly activeStepSubject = new BehaviorSubject<TutorialStep | null>(null);
  private readonly activeSubject = new BehaviorSubject(false);
  private readonly endedSubject = new Subject<TutorialEndEvent>();
  readonly activeStep$ = this.activeStepSubject.asObservable();
  readonly active$ = this.activeSubject.asObservable();
  readonly ended$ = this.endedSubject.asObservable();
  readonly steps = steps;
  private returnUrl = '/';
  private returnFocus: HTMLElement | null = null;
  private ending = false;
  private initialized = false;
  private mode: TutorialMode = 'replay';
  private terminalOutcome: TutorialOutcome | null = null;

  constructor() {
    this.tour.disableHotkeys();
    this.tour.end$.subscribe(() => this.cleanup());
  }

  registerAnchor(anchorId: string, element: HTMLElement): void {
    this.tour.register(anchorId, {
      element: { nativeElement: element },
      showTourStep: step => this.zone.run(() => this.activeStepSubject.next(step as TutorialStep)),
      hideTourStep: () => undefined
    });
  }

  unregisterAnchor(anchorId: string): void { this.tour.unregister(anchorId); }

  async start(mode: TutorialMode = 'replay'): Promise<void> {
    if (this.activeSubject.value) return;
    if (!this.initialized) {
      this.tour.initialize(this.steps, { enableBackdrop: false, smoothScroll: true, centerAnchorOnScroll: true, disablePageScrolling: false });
      this.initialized = true;
    }
    this.returnUrl = this.router.url;
    this.returnFocus = this.document.activeElement instanceof HTMLElement ? this.document.activeElement : null;
    this.mode = mode;
    this.terminalOutcome = null;
    this.activeSubject.next(true);
    this.activeStepSubject.next(null);
    if (this.router.url !== '/') {
      await this.router.navigateByUrl('/');
    }
    this.showAt(0);
  }

  next(): void { this.showAt(this.currentIndex() + 1); }
  previous(): void { this.showAt(this.currentIndex() - 1); }
  skip(): void { this.end('skipped'); }
  complete(): void { this.end('completed'); }

  end(outcome: TutorialOutcome): void {
    if (!this.activeSubject.value || this.ending) return;
    this.ending = true;
    this.terminalOutcome = outcome;
    this.tour.end();
  }

  private showAt(index: number): void {
    if (index < 0) return;
    if (index >= this.steps.length) { this.complete(); return; }
    this.activeStepSubject.next(this.steps[index]);
    window.setTimeout(() => this.tour.startAt(this.steps[index].stepId), 0);
  }

  private currentIndex(): number {
    const current = this.activeStepSubject.value;
    return current ? this.steps.findIndex(step => step.stepId === current.stepId) : 0;
  }

  private cleanup(): void {
    const url = this.returnUrl;
    const focus = this.returnFocus;
    const outcome = this.terminalOutcome;
    const mode = this.mode;
    this.activeStepSubject.next(null);
    this.activeSubject.next(false);
    this.ending = false;
    this.terminalOutcome = null;
    if (outcome) this.endedSubject.next({ mode, outcome });
    if (this.router.url !== url) {
      this.router.navigateByUrl(url).finally(() => window.setTimeout(() => focus?.focus(), 0));
    } else {
      window.setTimeout(() => focus?.focus(), 0);
    }
  }
}
